import {
    getAssignedSalesManagers,
    ZohoUserAssignmentPageKey,
} from '@internal/services/TSalesManager';
import { localeNamespace, TLocale } from '@internal/utils/machine/i18n/Locale';
import { Machine } from '@internal/utils/machine/Machine';
import { MachinePlaceholderImageMapper } from '@internal/utils/machine/placeholder/MachinePlaceholderImageMapper';
import { Close } from '@mui/icons-material';
import { Button } from '@renderer-ui-library/atoms';
import { CompareMachinesImageSlider } from '@renderer-ui-library/molecules';
import { Typography } from '@renderer-ui-library/mui';
import { ImageRatio } from '@renderer-ui-library/mui/utils/media/ImageRatio';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import { ContactWizardContext } from '@renderer-ui-library/organisms';
import { translations } from 'i18n/translations';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useContext, useMemo } from 'react';
import { MachineContext } from 'utils/machine/MachineContext';
import { formatPrice } from 'utils/numbers/formatPrice';
import { useMachineUrl } from 'utils/routing/hooks/useMachineUrl';
import { SalesManagerContext } from 'utils/SalesManager/SalesManagerContext';
import styles from './compareMachineHeader.module.scss';

interface CompareMachineHeaderProps {
    locale: TLocale;
    machine: Machine;
    removeMachine: (machineGroupId: string) => void;
    canRemove: boolean;
}

export const CompareMachineHeader: React.FC<CompareMachineHeaderProps> =
    React.memo(({ machine, removeMachine, locale, canRemove }) => {
        const { t } = useTranslation(localeNamespace.common);
        const { getMachineUrl } = useMachineUrl();
        const { salesManagers: allSalesManagers } =
            useContext(SalesManagerContext);
        const { isMobile } = useMediaQuery();
        const { updateMachine } = useContext(MachineContext);
        const { openRequestInfo } = useContext(ContactWizardContext);

        const salesManagers = useMemo(
            () =>
                getAssignedSalesManagers(
                    allSalesManagers,
                    ZohoUserAssignmentPageKey.ProductDetails,
                ),
            [allSalesManagers],
        );

        const onOpenRequestInfoClick = useCallback(() => {
            openRequestInfo();
            updateMachine(machine);
        }, [machine, openRequestInfo, updateMachine]);

        const removeMachineFromCompare = useCallback(
            () => removeMachine(machine.machineGroupId),
            [machine.machineGroupId, removeMachine],
        );

        const formattedPrice = machine.price
            ? formatPrice(locale, machine.price)
            : null;

        const placeholder = useMemo(
            () => [MachinePlaceholderImageMapper(machine.machineType, 'icon')],
            [machine.machineType],
        );

        return (
            <div className={styles.imageContainer}>
                <div className={styles.mediaContainer}>
                    {canRemove && (
                        <Close
                            className={styles.icon}
                            onClick={removeMachineFromCompare}
                        />
                    )}
                    <CompareMachinesImageSlider
                        images={
                            machine.media.length ? machine.media : placeholder
                        }
                        ratio={ImageRatio.Landscape}
                        autoPlay={false}
                        pagination={false}
                        arrows={!isMobile}
                    />
                </div>
                <div className={styles.headlineWrapper}>
                    <Link
                        href={getMachineUrl(locale, machine)}
                        passHref
                        prefetch={false}
                    >
                        <Typography variant="h5" noWrap>
                            {machine.name}
                        </Typography>

                        <Typography variant="body1" noWrap>
                            {formattedPrice
                                ? formattedPrice
                                : t(translations.priceOnRequest)}
                        </Typography>
                    </Link>
                </div>
                {salesManagers.length > 0 && (
                    <div className={styles.buttonWrapper}>
                        <Button
                            variant="outlined"
                            onClick={onOpenRequestInfoClick}
                        >
                            {t(translations.compareMachineContactButtonLabel)}
                        </Button>
                    </div>
                )}
            </div>
        );
    });

CompareMachineHeader.displayName = 'CompareMachineHeader';
